import { inject, Injectable } from '@angular/core';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ICampaignCreatorForm, ICtaForm, ICustomCtaValue } from '@campaign-form/campaign-form.interface';
import { CtaDetailsListService } from '@campaign-form/form-fields/creators-and-ctas/cta-details-list.service';
import {
  ICtaTemplateApi,
  ICtaTemplateApiDeliverables,
} from '@campaign-form/form-fields/creators-and-ctas/cta-dialog.interface';
import {
  TDeliverable,
  TDeliverableForm,
} from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.interface';
import { DeliverableListService } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/deliverable-step/deliverable-list/deliverable-list.service';
import {
  EExecutionType,
  IExecution,
  IExecutionForm,
} from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution/execution.interface';
import { ExecutionService } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/execution-step/execution-list/execution/execution.service';
import { ExecutionExpectedKpisService } from '@campaign-form/form-fields/creators-and-ctas/deliverable-flow-dialog/expected-kpi-step/execution-expected-kpi-list/execution-expected-kpis/execution-expected-kpis.service';
import { EDeliverableType, EPlatformFeature } from '@enums/campaign.enum';
import { ICampaignCreator, IGlobalTracking, TCampaignApi } from '@pages/campaigns/campaigns.interface';
import { ICreator } from '@pages/creators/creators.interface';
import { ALL_CREATORS_ID } from '@shared/constants/all-creators.constants';
import { DateUtilitiesService } from '@utilities/date-utilities.service';
import { IconUtilitiesService } from '@utilities/icon-utilities.service';
import { SocialUtilitiesService } from '@utilities/social-utilities.service';
import { camelCase } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { TCtaTemplateSort } from './creators-and-ctas.interface';

/**
 * Maps every API deliverable field to its `EDeliverableType`.
 * `Record<keyof ICtaTemplateApiDeliverables, …>` requires all keys — adding a field
 * to `ICtaTemplateApiDeliverables` without an entry here is a compile error.
 */
const API_DELIVERABLE_TYPE_MAP: Record<keyof ICtaTemplateApiDeliverables, EDeliverableType> = {
  audioTrackings: EDeliverableType.audioTracking,
  captions: EDeliverableType.caption,
  chatbotMultis: EDeliverableType.chatbotMulti,
  chatbots: EDeliverableType.chatBot,
  contents: EDeliverableType.content,
  customs: EDeliverableType.custom,
  descriptions: EDeliverableType.description,
  gamePlays: EDeliverableType.gamePlay,
  hashTags: EDeliverableType.hashTag,
  mentions: EDeliverableType.mention,
  messages: EDeliverableType.message,
  productPlacements: EDeliverableType.productPlacement,
  showcases: EDeliverableType.showcase,
  talkingPointAis: EDeliverableType.talkingPointAi,
  talkingPoints: EDeliverableType.talkingPoint,
  textTrackings: EDeliverableType.textTracking,
  titles: EDeliverableType.title,
  visuals: EDeliverableType.visual,
};

@Injectable({
  providedIn: 'root',
})
export class CreatorsAndCtasService {
  private readonly deliverableListService = inject(DeliverableListService);
  private readonly executionService = inject(ExecutionService);
  private readonly dateUtils = inject(DateUtilitiesService);
  private readonly ctaDetailsListService = inject(CtaDetailsListService);

  public static getApiDeliverables(cta: ICtaTemplateApi | IGlobalTracking): TDeliverable[] {
    const result: TDeliverable[] = [];

    for (const [key, type] of Object.entries(API_DELIVERABLE_TYPE_MAP) as Array<
      [keyof ICtaTemplateApiDeliverables, EDeliverableType]
    >) {
      if (!(key in cta)) {
        continue;
      }

      const items = (cta as ICtaTemplateApi)[key] as Array<object> | null | undefined;

      if (items) {
        for (const item of items) {
          result.push({ type, ...item } as TDeliverable);
        }
      }
    }

    return result;
  }

  public getExistingDeliverablesCount(
    deliverables: FormArray<FormGroup<TDeliverableForm>>['value'],
    creatorId: string,
  ): number {
    let existingDeliverableCount = 0;

    deliverables?.forEach((deliverable) => {
      const customCtaValues = deliverable.customCtaValues as ICustomCtaValue[] | null;
      const isDeleted = customCtaValues?.some((value) => value.creatorId === creatorId && value.valueName === null);
      const { exclusiveTo } = deliverable;
      const hasDeliverable = !isDeleted && (!exclusiveTo || exclusiveTo === creatorId);

      existingDeliverableCount += Number(hasDeliverable);
    });

    return existingDeliverableCount;
  }

  public getExistingExecutionsAndKpisCount(
    cta: FormGroup<ICtaForm>['value'],
    creatorId: string,
  ): [number | null, number] {
    let executionsCount: number | null = null;
    let kpisCount = 0;

    cta.executions?.forEach((execution) => {
      const customCtaValues = execution.customCtaValues as ICustomCtaValue[] | null;
      const isDeleted = customCtaValues?.some((value) => value.creatorId === creatorId && value.valueName === null);
      const { exclusiveTo } = execution;
      const hasExecution = !isDeleted && (!exclusiveTo || exclusiveTo === creatorId);

      kpisCount += this.getKpisCount(execution, creatorId);

      if (hasExecution) {
        executionsCount =
          (executionsCount ?? 0) +
          this.getExecutionCountBasedOnType(execution, hasExecution, customCtaValues, creatorId);
      }
    });

    return [executionsCount, kpisCount];
  }

  public createCtaTemplateFormsFromCampaign(
    campaign: TCampaignApi | null,
    isViewMode: boolean,
  ): {
    ctaTemplates: ICtaTemplateApi[];
    ctaTemplateForms: Map<string, FormGroup<ICtaForm>>;
  } {
    const ctaTemplateForms = new Map<string, FormGroup<ICtaForm>>();
    const ctaTemplates = campaign?.ctaTemplates || [];

    ctaTemplates.forEach((cta) => {
      if (ctaTemplateForms.has(cta.id!)) {
        return;
      }

      const deliverables = CreatorsAndCtasService.getApiDeliverables(cta);
      const executions = this.getExecutions(cta);

      const ctaGroup = this.createCtaFormFromValues(cta, deliverables, executions ?? [], isViewMode);
      ctaTemplateForms.set(cta.id!, ctaGroup);
    });

    return { ctaTemplateForms, ctaTemplates };
  }

  public createCtaFormFromCtaValues(ctaValues: FormGroup<ICtaForm>['value']): FormGroup<ICtaForm> {
    const deliverables = ctaValues.deliverables as TDeliverable[];
    const executions = this.getExecutions(ctaValues);

    return this.createCtaFormFromValues(ctaValues, deliverables, executions ?? [], false);
  }

  public createCtaFormFromValues(
    cta: FormGroup<ICtaForm>['value'] | ICtaTemplateApi,
    deliverables: TDeliverable[],
    executions: FormGroup<IExecutionForm>[],
    isViewMode: boolean,
  ): FormGroup<ICtaForm> {
    return new FormGroup<ICtaForm>({
      customCtaValues: new FormControl(cta.customCtaValues ?? [], { nonNullable: true }),
      deliverables: new FormArray<FormGroup<TDeliverableForm>>(
        deliverables.map(
          (deliverable) =>
            this.deliverableListService.createDeliverable(
              deliverable.type,
              deliverable as TDeliverable,
              undefined,
              isViewMode,
            )!,
        ) ?? [],
      ),
      exclusiveTo: new FormControl(cta.exclusiveTo),
      executions: new FormArray<FormGroup<IExecutionForm>>(executions ?? []),
      icon: new FormControl(IconUtilitiesService.getPlatformFeatureIcon(cta.platformFeature!)),
      id: new FormControl(cta.id ?? uuidv4()),
      isAllCreators: new FormControl(cta.isAllCreators ?? false),
      isCtaDetailsValid: new FormControl(true),
      isCustomMode: new FormControl(false),
      name: new FormControl(cta.name ?? '', [Validators.required, Validators.maxLength(255)]),
      platformFeature: new FormControl(camelCase(cta.platformFeature!) as EPlatformFeature, {
        nonNullable: true,
      }),
    });
  }

  public assignCtaTemplateFormsToCreators(
    newCampaignCreators: ICreator[],
    ctaTemplates: ICtaTemplateApi[],
    ctaTemplateForms: Map<string, FormGroup<ICtaForm>>,
    campaignCreatorsArray: FormArray<FormGroup<ICampaignCreatorForm>>,
    campaign: TCampaignApi | null,
  ): void {
    newCampaignCreators.forEach((creator) => {
      const campaignCreator = campaign?.campaignCreators?.find((c) => c.creatorId === creator.id);
      const ctasFormArray = new FormArray<FormGroup<ICtaForm>>([]);

      const existingCampaignCreatorCtas = ctaTemplates.filter(
        (ctaTemplate) =>
          ctaTemplate.ctaCreators?.some((ctaCreator) => ctaCreator.creatorId === creator.id) ||
          ctaTemplate.isAllCreators,
      );

      existingCampaignCreatorCtas.forEach((cta) => {
        const existingForm = ctaTemplateForms.get(cta.id!);

        if (existingForm) {
          ctasFormArray.push(existingForm);
        }
      });

      campaignCreatorsArray.push(this.getFilledCampaignCreatorForm(campaignCreator, creator, campaign));
    });
  }

  public getFilledCampaignCreatorForm(
    campaignCreator: ICampaignCreator | undefined,
    creator: ICreator,
    campaign: TCampaignApi | null,
  ): FormGroup<ICampaignCreatorForm> {
    return new FormGroup<ICampaignCreatorForm>({
      campaignId: new FormControl(campaign?.id || ''),
      cost: new FormControl<string | null>(campaignCreator?.cost ?? null, [Validators.min(0)]),
      creator: new FormControl<ICreator | null>(creator),
      creatorId: new FormControl(creator.id),
      ctas: new FormArray<FormGroup<ICtaForm>>([]),
      id: new FormControl(campaignCreator?.id ?? `${creator.id}-${uuidv4()}`),
    });
  }

  public getExecutions(cta: ICtaTemplateApi | FormGroup<ICtaForm>['value']): FormGroup<IExecutionForm>[] | undefined {
    return cta.executions?.map((execution) =>
      this.executionService.getConfig().createForm(cta.platformFeature!, {
        ...execution,
        executionDate: execution.executionDate
          ? execution.executionDate instanceof Date
            ? execution.executionDate
            : this.dateUtils.parseDateTimeFromApi(execution.executionDate)
          : null,
        executionTime: execution.executionTime
          ? execution.executionTime instanceof Date
            ? execution.executionTime
            : this.dateUtils.parseDateTimeFromApi(execution.executionTime)
          : null,
      } as IExecution),
    );
  }

  public updateCtaInformationCache(
    campaignCreatorsArray: FormArray<FormGroup<ICampaignCreatorForm>>['value'],
    deliverablesCountCache: Map<string, number>,
    executionsCountCache: Map<string, number | null>,
    kpisCountCache: Map<string, number>,
    allSharedCtas: FormGroup<ICtaForm>['value'][],
  ): void {
    deliverablesCountCache.clear();
    executionsCountCache.clear();
    kpisCountCache.clear();

    allSharedCtas.forEach((cta) => {
      const deliverablesCount = this.getExistingDeliverablesCount(cta.deliverables!, ALL_CREATORS_ID);
      const [executionsCount, kpisCount] = this.getExistingExecutionsAndKpisCount(cta, ALL_CREATORS_ID);
      const cacheKey = `${cta.id}_${ALL_CREATORS_ID}`;

      deliverablesCountCache.set(cacheKey, deliverablesCount);
      executionsCountCache.set(cacheKey, executionsCount);
      kpisCountCache.set(cacheKey, kpisCount);
    });

    campaignCreatorsArray.forEach((creatorForm) => {
      const creatorId = creatorForm.creatorId!;
      const ctasArray = creatorForm.ctas;

      ctasArray?.forEach((cta) => {
        const deliverablesCount = this.getExistingDeliverablesCount(cta.deliverables ?? [], creatorId);
        const [executionsCount, kpisCount] = this.getExistingExecutionsAndKpisCount(cta, creatorId);
        const cacheKey = `${cta.id}_${creatorId}`;

        deliverablesCountCache.set(cacheKey, deliverablesCount);
        executionsCountCache.set(cacheKey, executionsCount);
        kpisCountCache.set(cacheKey, kpisCount);
      });
    });
  }

  public getGlobalAndSharedCtas(
    campaignCreators: FormGroup<ICampaignCreatorForm>['value'][],
    ctaValues: FormGroup<ICtaForm>['value'][],
  ): [FormGroup<ICtaForm>['value'][], Map<string, number>] {
    const ctaUsageCount = new Map<string, number>();
    const sharedCtas: FormGroup<ICtaForm>['value'][] = [];
    const addedCtaIds = new Set<string>();

    campaignCreators.forEach((campaignCreator) => {
      campaignCreator?.ctas?.forEach((cta) => {
        if (cta.isAllCreators) {
          return;
        }

        const ctaId = cta.id!;

        ctaUsageCount.set(ctaId, (ctaUsageCount.get(ctaId) ?? 0) + 1);

        if (ctaUsageCount.get(ctaId)! > 1 && !addedCtaIds.has(ctaId)) {
          sharedCtas.push(cta);
          addedCtaIds.add(ctaId);
        }
      });
    });

    return [[...ctaValues.filter((cta) => cta.isAllCreators), ...sharedCtas], ctaUsageCount];
  }

  public sortCtaTemplates<T>(ctas: (TCtaTemplateSort & T)[]): T[] {
    return ctas.sort((a, b) => {
      const orderA: number =
        this.ctaDetailsListService.platformFeatureOrder[camelCase(a.platformFeature) as EPlatformFeature];
      const orderB: number =
        this.ctaDetailsListService.platformFeatureOrder[camelCase(b.platformFeature) as EPlatformFeature];

      if (orderA !== orderB) {
        return orderA - orderB;
      }

      return a.name?.localeCompare(b.name ?? '') ?? 0;
    });
  }

  public sortCampaignCreatorsValue(
    campaignCreators: FormGroup<ICampaignCreatorForm>['value'][],
  ): FormGroup<ICampaignCreatorForm>['value'][] {
    return campaignCreators.sort((a, b) => {
      const nameA = a.creator?.name || '';
      const nameB = b.creator?.name || '';

      return nameA.localeCompare(nameB);
    });
  }

  public updateCampaignCreatorsCtaValues(
    campaignCreators: FormGroup<ICampaignCreatorForm>['value'][],
    creatorIds: string[],
    cta: FormGroup<ICtaForm>['value'],
  ): void {
    const ctaId = cta.id!;
    const targetCreatorIds: string[] = cta.isAllCreators
      ? this.getCompatibleCreatorIds(campaignCreators, cta.platformFeature!)
      : creatorIds;

    this.removeCtaFromUnselectedOrIncompatibleCreators(campaignCreators, targetCreatorIds, ctaId, cta.platformFeature!);
    this.insertCtaToSelectedCreators(campaignCreators, targetCreatorIds, cta);
  }

  public canSaveCreatorAndCtas(
    campaignCreators: FormGroup<ICampaignCreatorForm>['value'][],
    deliverablesCountCache: Map<string, number>,
    executionsCountCache: Map<string, number | null>,
    globalAndSharedCtaValues: FormGroup<ICtaForm>['value'][],
  ): boolean {
    const everyCreatorIsValid = campaignCreators.every((creator) =>
      creator.ctas?.every((cta) => {
        const deliverablesCount = deliverablesCountCache.get(`${cta.id}_${creator.creatorId!}`);
        const executionsCount = executionsCountCache.get(`${cta.id}_${creator.creatorId!}`);

        return deliverablesCount !== 0 && executionsCount !== null;
      }),
    );

    const everyGlobalAndSharedCtaIsValid = globalAndSharedCtaValues.every(
      (cta) =>
        cta.deliverables?.some((deliverable) => !deliverable.exclusiveTo) &&
        cta.executions?.some((execution) => !execution.exclusiveTo),
    );

    return everyCreatorIsValid && everyGlobalAndSharedCtaIsValid;
  }

  public addGlobalCtasToNewCreators(
    campaignCreators: FormGroup<ICampaignCreatorForm>['value'][],
    addedCreatorIds: string[],
    globalAndSharedCtas: FormGroup<ICtaForm>['value'][],
  ): void {
    campaignCreators.forEach((campaignCreator) => {
      if (!addedCreatorIds.includes(campaignCreator.creatorId!)) {
        return;
      }

      globalAndSharedCtas.forEach((cta) => {
        if (
          !cta.isAllCreators ||
          !SocialUtilitiesService.isPlatformCompatible(
            cta.platformFeature!,
            undefined,
            campaignCreator.creator?.channels,
          ) ||
          campaignCreator.ctas?.some((c) => c.id === cta.id)
        ) {
          return;
        }

        if (campaignCreator.ctas) {
          campaignCreator.ctas.push(cta);
        } else {
          campaignCreator.ctas = [cta];
        }
      });

      this.sortCtaTemplates(campaignCreator.ctas as TCtaTemplateSort[]);
    });
  }

  private getKpisCount(execution: FormGroup<IExecutionForm>['value'], creatorId: string): number {
    let kpisCount = 0;

    execution.expectedKpis?.forEach((expectedKpi) => {
      const customCtaValues = expectedKpi.customCtaValues as ICustomCtaValue[] | null;
      const isDeleted = customCtaValues?.some((value) => value.creatorId === creatorId && value.valueName === null);
      const { exclusiveTo, expectedValue } = expectedKpi;
      const hasExecution = !isDeleted && (!exclusiveTo || exclusiveTo === creatorId);

      if (!hasExecution) {
        return;
      }

      const creatorCustomValue = customCtaValues?.find(
        (value) => value.creatorId === creatorId && value.valueName === 'expected_value',
      );

      // A shared row only counts for the creator when they actually hold a value for it - their
      // custom value, or the shared value when they have no override. Without this, a KPI goal
      // added in one creator's custom mode (shared row + per-creator custom value) would count
      // as a goal for every other creator too.
      const creatorHasValue = creatorCustomValue
        ? ExecutionExpectedKpisService.isExpectedValueSet(creatorCustomValue.value)
        : ExecutionExpectedKpisService.isExpectedValueSet(expectedValue);

      kpisCount += Number(creatorHasValue);
    });

    return kpisCount;
  }

  private getExecutionCountBasedOnType(
    execution: FormGroup<IExecutionForm>['value'],
    hasExecution: boolean,
    customCtaValues: ICustomCtaValue[] | null,
    creatorId: string,
  ): number {
    const { executionType } = execution;

    switch (executionType) {
      case EExecutionType.undesignated:
        return hasExecution
          ? Number(
              customCtaValues?.find(
                (value) => value.creatorId === creatorId && value.valueName === 'execution_count_target',
              )?.value ?? execution.executionCountTarget,
            )
          : (execution.executionCountTarget ?? 1);
      case EExecutionType.always:
        return 0;
      case EExecutionType.unique:
        return 1;
      default:
        return 1;
    }
  }

  private removeCtaFromUnselectedOrIncompatibleCreators(
    campaignCreators: FormGroup<ICampaignCreatorForm>['value'][],
    creatorIds: string[],
    ctaId: string,
    platformFeature: EPlatformFeature,
  ): void {
    campaignCreators.forEach((campaignCreator) => {
      const isSelectedCreator: boolean = creatorIds.includes(campaignCreator.creatorId!);
      const isPlatformCompatible: boolean = SocialUtilitiesService.isPlatformCompatible(
        platformFeature,
        undefined,
        campaignCreator.creator?.channels,
      );

      if (isSelectedCreator && isPlatformCompatible) {
        return;
      }

      if (!campaignCreator.ctas?.length) {
        return;
      }

      const removeIndex: number = campaignCreator.ctas.findIndex((existingCta) => existingCta.id === ctaId);

      if (removeIndex !== -1) {
        campaignCreator.ctas.splice(removeIndex, 1);
      }
    });
  }

  private insertCtaToSelectedCreators(
    campaignCreators: FormGroup<ICampaignCreatorForm>['value'][],
    creatorIds: string[],
    cta: FormGroup<ICtaForm>['value'],
  ): void {
    campaignCreators.forEach((campaignCreator) => {
      if (
        !creatorIds.includes(campaignCreator.creatorId!) ||
        !SocialUtilitiesService.isPlatformCompatible(cta.platformFeature!, undefined, campaignCreator.creator?.channels)
      ) {
        return;
      }

      if (!campaignCreator.ctas) {
        campaignCreator.ctas = [cta];

        return;
      }

      const platformOrderMap: Record<EPlatformFeature, number> = this.ctaDetailsListService.platformFeatureOrder;
      const newCtaOrder: number = platformOrderMap[camelCase(cta.platformFeature) as EPlatformFeature];
      const existingIndex: number = campaignCreator.ctas.findIndex((existingCta) => {
        const existingId: string | null = existingCta.id ?? null;
        const newId: string | null = cta.id ?? null;

        if (existingId && newId) {
          return existingId === newId;
        }

        const samePlatform: boolean = camelCase(existingCta.platformFeature) === camelCase(cta.platformFeature);

        if (!samePlatform) {
          return false;
        }

        const newName: string = cta.name ?? '';
        const existingName: string = existingCta.name ?? '';

        return newName.localeCompare(existingName) === 0;
      });

      if (existingIndex !== -1) {
        campaignCreator.ctas.splice(existingIndex, 1);
      }

      const insertAtIndex: number = campaignCreator.ctas.findIndex((existingCta) => {
        const existingOrder: number = platformOrderMap[camelCase(existingCta.platformFeature) as EPlatformFeature];

        if (newCtaOrder !== existingOrder) {
          return newCtaOrder < existingOrder;
        }

        const newName: string = cta.name ?? '';
        const existingName: string = existingCta.name ?? '';

        return newName.localeCompare(existingName) <= 0;
      });

      if (insertAtIndex === -1) {
        campaignCreator.ctas.push(cta);

        return;
      }

      campaignCreator.ctas.splice(insertAtIndex, 0, cta);
    });
  }

  private getCompatibleCreatorIds(
    campaignCreators: FormGroup<ICampaignCreatorForm>['value'][],
    platformFeature: EPlatformFeature,
  ): string[] {
    return campaignCreators.reduce((accumulator: string[], campaignCreator) => {
      if (
        campaignCreator.creatorId &&
        SocialUtilitiesService.isPlatformCompatible(platformFeature, undefined, campaignCreator.creator?.channels)
      ) {
        accumulator.push(campaignCreator.creatorId);
      }

      return accumulator;
    }, []);
  }
}
